import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { downloadExport } from "@/components/TemplatesDialog";
import { BarChart3, Download } from "lucide-react";

const eur = (v) => (v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";

const StatTable = ({ title, rows, testId }) => (
  <Card>
    <CardContent className="p-5">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-3">Nom</th>
              <th className="py-2 pr-3 text-right">Prospects</th>
              <th className="py-2 pr-3 text-right">Contactés</th>
              <th className="py-2 pr-3 text-right">Réponses</th>
              <th className="py-2 pr-3 text-right">Taux rép.</th>
              <th className="py-2 pr-3 text-right">Positives</th>
              <th className="py-2 pr-3 text-right">Opport.</th>
              <th className="py-2 pr-3 text-right">CA potentiel</th>
              <th className="py-2 text-right">CA gagné</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-secondary/40">
                <td className="max-w-[220px] truncate py-2 pr-3 font-medium">{r.nom}</td>
                <td className="py-2 pr-3 text-right">{r.prospects}</td>
                <td className="py-2 pr-3 text-right">{r.contactes}</td>
                <td className="py-2 pr-3 text-right">{r.reponses}</td>
                <td className="py-2 pr-3 text-right font-medium">{r.taux_reponse}%</td>
                <td className="py-2 pr-3 text-right text-green-700">{r.reponses_positives}</td>
                <td className="py-2 pr-3 text-right">{r.opportunites}{r.gagnees ? ` (${r.gagnees} 🏆)` : ""}</td>
                <td className="py-2 pr-3 text-right text-accent">{eur(r.ca_potentiel)}</td>
                <td className="py-2 text-right font-semibold text-green-700">{eur(r.ca_gagne)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export default function Stats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const g = stats?.global;
  const funnel = g ? [
    { label: "Prospects en base", value: g.prospects },
    { label: "Contactés", value: g.contactes },
    { label: "E-mails envoyés", value: g.emails_envoyes },
    { label: "Réponses", value: g.reponses },
    { label: "Réponses positives", value: g.reponses_positives },
    { label: "Opportunités", value: g.opportunites },
    { label: "Gagnées", value: g.gagnees },
  ] : [];

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="stats-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display flex items-center gap-2 text-3xl font-semibold sm:text-4xl"><BarChart3 className="text-accent" /> Statistiques</h1>
          <p className="mt-1 text-sm text-muted-foreground">Performance de la prospection par campagne et par secteur.</p>
        </div>
        <Button variant="outline" onClick={() => downloadExport("/export/stats.xlsx", "renforteo_statistiques.xlsx")} data-testid="export-stats-button">
          <Download size={15} className="mr-1.5" /> Export Excel
        </Button>
      </div>

      {!stats ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="stats-funnel">
            {funnel.map((f) => (
              <Card key={f.label} className="kpi-card">
                <CardContent className="p-3 text-center">
                  <p className="font-display text-2xl font-semibold">{f.value}</p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{f.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Card className="kpi-card"><CardContent className="p-4 text-center">
              <p className="font-display text-3xl font-semibold">{g.taux_reponse}%</p>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Taux de réponse global</p>
            </CardContent></Card>
            <Card className="kpi-card"><CardContent className="p-4 text-center">
              <p className="font-display text-3xl font-semibold text-accent">{eur(g.ca_potentiel)}</p>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">CA potentiel en cours</p>
            </CardContent></Card>
            <Card className="kpi-card"><CardContent className="p-4 text-center">
              <p className="font-display text-3xl font-semibold text-green-700">{eur(g.ca_gagne)}</p>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">CA gagné</p>
            </CardContent></Card>
          </div>

          <StatTable title="Performance par campagne" rows={stats.par_campagne} testId="stats-campaigns-table" />
          <StatTable title="Performance par secteur" rows={stats.par_secteur} testId="stats-sectors-table" />
        </>
      )}
    </div>
  );
}
